import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FaBookOpen, 
  FaFileAlt, 
  FaRegLightbulb, 
  FaCheckSquare, 
  FaBookReader, 
  FaAward, 
  FaChevronRight, 
  FaQuoteRight 
} from 'react-icons/fa';
import '../styles/cards.css';

import { supabase } from '../config/supabaseClient';
import { Spinner, Container } from 'react-bootstrap';
import { RecentPostsSection } from './recentpost';

const RECENT_POSTS_LIMIT = 9;

const createSlug = (str) =>
  str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/--+/g, '-')
    .trim();

const FeaturedClasses = () => {
  const navigate = useNavigate();
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [error, setError] = useState(null);

  const handleCardClick = (className) => {
    navigate(`/notes/${className.toLowerCase().replace(" ", "")}`);
  };

  // Fetch counts and recent posts
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // 1. Fetch all topics for counting
      const { data: topicsData, error: topicsError } = await supabase
        .from('topics')
        .select('class, subCategory');
      
      if (topicsError) throw topicsError;

      const countMap = {};
      (topicsData || []).forEach(doc => {
        const classesList = (doc.class || '').split(',').map(s => s.trim());
        const subCat = doc.subCategory || '';

        classesList.forEach(cls => {
          const classNumMatch = cls.match(/\d+/);
          const classKey = classNumMatch ? classNumMatch[0] : cls;

          const key = `${classKey}_${subCat}`;
          countMap[key] = (countMap[key] || 0) + 1;
          
          // Grammar checking
          const isGrammarWord = (word) => [
            'grammar', 'applied grammar', 'grammar topics', 'letters', 'essays', 
            'stories', 'applications', 'translation', 'idioms', 
            'direct & indirect'
          ].includes(word.toLowerCase());

          if (isGrammarWord(cls) || isGrammarWord(subCat)) {
            const catName = isGrammarWord(subCat) ? subCat : cls;
            let normalizedCat = catName;
            if (catName.toLowerCase().startsWith('letter')) normalizedCat = 'Letters';
            if (catName.toLowerCase().startsWith('essay')) normalizedCat = 'Essays';
            if (catName.toLowerCase().startsWith('application')) normalizedCat = 'Applications';
            if (
              catName.toLowerCase().startsWith('grammar') ||
              catName.toLowerCase() === 'applied grammar' ||
              catName.toLowerCase() === 'grammar topics'
            ) {
              normalizedCat = 'Applied Grammar';
            }

            const grammarKey = `grammar_${normalizedCat}`;
            countMap[grammarKey] = (countMap[grammarKey] || 0) + 1;
          }
        });
      });
      setCounts(countMap);

      // 2. Fetch recent posts
      const { data: postsData, error: postsError } = await supabase
        .from('topics')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(RECENT_POSTS_LIMIT);

      if (postsError) throw postsError;

      const posts = postsData.map((data) => {
        let imageUrl = data.featuredImage || data.imageUrl || '';

        return {
          ...data,
          timestamp: data.timestamp ? new Date(data.timestamp) : null,
          topicId: data.id,
          imageUrl,
          slug: createSlug(data.topic),
        };
      }).filter((post) => post.topic);

      setRecentPosts(posts);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load portal content.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <section className="featured-section">
      <div className="featured-container">
        {/* Heading Section */}
        <div className="top-text-center mb-5">
          <div className="heading-wrapper">
            <h2 className="section-heading text-center">Featured Classes</h2>
            <div className="gradient-underline"></div>
          </div>
          <p className="description max-w-2xl mx-auto">
            Explore expertly crafted courses, tailored study content, and interactive tools designed to empower your educational journey.
          </p>
        </div>

        {/* Premium Grid Layout */}
        <div className="horizontal-card-container">
          
          {/* Class 9 Card */}
          <div className="class-card theme-blue">
            <div className="card-badge">Class 9</div>
            <div className="card-image-wrapper" onClick={() => handleCardClick("9")}>
              9
            </div>
            <div className="card-divider"></div>
            <div className="card-links-list">
              <Link to="/notes/9/Book Lessons" className="custom-card-item">
                <span className="link-icon-box"><FaBookOpen /></span>
                <span className="link-text">Notes</span>
                <span className="link-count-pill">{counts["9_Book Lessons"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/9/Past Papers" className="custom-card-item">
                <span className="link-icon-box"><FaFileAlt /></span>
                <span className="link-text">Past Papers</span>
                <span className="link-count-pill">{counts["9_Past Papers"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/9/Model Papers" className="custom-card-item">
                <span className="link-icon-box"><FaRegLightbulb /></span>
                <span className="link-text">Guess Papers</span>
                <span className="link-count-pill">{counts["9_Model Papers"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/9/MCQ Test" className="custom-card-item">
                <span className="link-icon-box"><FaCheckSquare /></span>
                <span className="link-text">MCQs Test</span>
                <span className="link-count-pill">{counts["9_MCQ Test"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
            </div>
          </div>

          {/* Class 10 Card */}
          <div className="class-card theme-emerald">
            <div className="card-badge">Class 10</div>
            <div className="card-image-wrapper" onClick={() => handleCardClick("10")}>
              10
            </div>
            <div className="card-divider"></div>
            <div className="card-links-list">
              <Link to="/notes/10/Book Lessons" className="custom-card-item">
                <span className="link-icon-box"><FaBookOpen /></span>
                <span className="link-text">Notes</span>
                <span className="link-count-pill">{counts["10_Book Lessons"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/10/Past Papers" className="custom-card-item">
                <span className="link-icon-box"><FaFileAlt /></span>
                <span className="link-text">Past Papers</span>
                <span className="link-count-pill">{counts["10_Past Papers"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/10/Model Papers" className="custom-card-item">
                <span className="link-icon-box"><FaRegLightbulb /></span>
                <span className="link-text">Guess Papers</span>
                <span className="link-count-pill">{counts["10_Model Papers"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/10/MCQ Test" className="custom-card-item">
                <span className="link-icon-box"><FaCheckSquare /></span>
                <span className="link-text">MCQs Test</span>
                <span className="link-count-pill">{counts["10_MCQ Test"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
            </div>
          </div>

          {/* Class 11 Card */}
          <div className="class-card theme-orange">
            <div className="card-badge">Class 11</div>
            <div className="card-image-wrapper" onClick={() => handleCardClick("11")}>
              11
            </div>
            <div className="card-divider"></div>
            <div className="card-links-list">
              <Link to="/notes/11/Book Lessons" className="custom-card-item">
                <span className="link-icon-box"><FaBookOpen /></span>
                <span className="link-text">Notes</span>
                <span className="link-count-pill">{counts["11_Book Lessons"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/11/Past Papers" className="custom-card-item">
                <span className="link-icon-box"><FaFileAlt /></span>
                <span className="link-text">Past Papers</span>
                <span className="link-count-pill">{counts["11_Past Papers"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/11/Model Papers" className="custom-card-item">
                <span className="link-icon-box"><FaRegLightbulb /></span>
                <span className="link-text">Guess Papers</span>
                <span className="link-count-pill">{counts["11_Model Papers"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/11/MCQ Test" className="custom-card-item">
                <span className="link-icon-box"><FaCheckSquare /></span>
                <span className="link-text">MCQs Test</span>
                <span className="link-count-pill">{counts["11_MCQ Test"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
            </div>
          </div>

          {/* Class 12 Card */}
          <div className="class-card theme-purple">
            <div className="card-badge">Class 12</div>
            <div className="card-image-wrapper" onClick={() => handleCardClick("12")}>
              12
            </div>
            <div className="card-divider"></div>
            <div className="card-links-list">
              <Link to="/notes/12/Book Lessons" className="custom-card-item">
                <span className="link-icon-box"><FaBookOpen /></span>
                <span className="link-text">Notes</span>
                <span className="link-count-pill">{counts["12_Book Lessons"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/12/Past Papers" className="custom-card-item">
                <span className="link-icon-box"><FaFileAlt /></span>
                <span className="link-text">Past Papers</span>
                <span className="link-count-pill">{counts["12_Past Papers"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/12/Model Papers" className="custom-card-item">
                <span className="link-icon-box"><FaRegLightbulb /></span>
                <span className="link-text">Guess Papers</span>
                <span className="link-count-pill">{counts["12_Model Papers"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/notes/12/MCQ Test" className="custom-card-item">
                <span className="link-icon-box"><FaCheckSquare /></span>
                <span className="link-text">MCQs Test</span>
                <span className="link-count-pill">{counts["12_MCQ Test"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
            </div>
          </div>

          {/* Grammar & Composition Card */}
          <div className="class-card theme-indigo">
            <div className="card-badge">Grammar</div>
            <div className="card-image-wrapper" onClick={() => handleCardClick("grammar")}>
              G
            </div>
            <div className="card-divider"></div>
            <div className="card-links-list">
              <Link to="/sections/Letters" className="custom-card-item">
                <span className="link-icon-box"><FaFileAlt /></span>
                <span className="link-text">Letters</span>
                <span className="link-count-pill">{counts["grammar_Letters"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/sections/Applications" className="custom-card-item">
                <span className="link-icon-box"><FaBookReader /></span>
                <span className="link-text">Applications</span>
                <span className="link-count-pill">{counts["grammar_Applications"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/sections/Essays" className="custom-card-item">
                <span className="link-icon-box"><FaQuoteRight /></span>
                <span className="link-text">Essay Writing</span>
                <span className="link-count-pill">{counts["grammar_Essays"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
              <Link to="/sections/Applied Grammar" className="custom-card-item">
                <span className="link-icon-box"><FaAward /></span>
                <span className="link-text">Applied Grammar</span>
                <span className="link-count-pill">{counts["grammar_Applied Grammar"] || 0}</span>
                <span className="link-arrow"><FaChevronRight /></span>
              </Link>
            </div>
          </div>

        </div>

        {/* Recent Posts */}
        <div className="recent-section mt-5">
          <div className="heading-wrapper">
            <h2 className="section-heading text-center">Recent Posts</h2>
            <div className="gradient-underline"></div>
          </div>
          <p className="description mb-4">
            Discover the most recently added content across all classes and topics.
          </p>

          {loading ? (
            <Container className="my-4 text-center">
              <Spinner animation="border" variant="primary" />
            </Container>
          ) : error ? (
            <Container className="my-4 text-center text-danger">
              {error}
            </Container>
          ) : (
            <RecentPostsSection recentPosts={recentPosts} loading={false} error={null} />
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedClasses;
